import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { PasswordField } from 'seagull';
import { routes } from '~/constants';
import { useStore, useStoreActions } from '~/contexts';
import { usePasswordStrength } from '~/hooks/usePasswordStrength';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useLocaleKeys } from '~/i18n';
import { handlePromise } from '~/infra/utils';
import { NotificationActionType } from '~/pages/NotificationAction';
import { validatePassword } from '~/utils/password-policy';
import { FormTitle, FormFields, ErrorMessage, FormContainer, SubmitButtonField, VerticalFormFields } from '../components/FormComponents';
import UnauthorizedPageContainer from '../components/UnauthorizedPageContainer';
export function SetPasswordScreen() {
    const { t: texts } = useLocaleKeys('pages.auth.setPassword');
    const history = useHistory();
    const location = useLocation();
    const locationState = location.state;
    const { logBI, api } = useStoreActions();
    const claims = useStore((state)=>state.claims);
    const [passwordSetInProgress, setPasswordSetInProgress] = useState(false);
    const [errorMessage, setErrorMessage] = useState();
    const { handleSubmit, control, formState, getValues, watch } = useForm({
        mode: 'onChange'
    });
    const passwordValue = watch('password', '');
    const userInputs = [
        claims === null || claims === void 0 ? void 0 : claims.email,
        claims === null || claims === void 0 ? void 0 : claims.firstName,
        claims === null || claims === void 0 ? void 0 : claims.lastName
    ].filter(Boolean);
    const strengthScore = usePasswordStrength(passwordValue, userInputs);
    useSetDocumentTitle('Anchor Reset Password');
    const onSubmit = handleSubmit(async (data, e)=>{
        logBI(10601);
        e === null || e === void 0 ? void 0 : e.preventDefault();
        setPasswordSetInProgress(true);
        const { password } = data;
        const request = api.auth.setPassword;
        const result = await handlePromise(request(password));
        if (!result.success) {
            setPasswordSetInProgress(false);
            setErrorMessage('An error occurred');
            const subscription = watch(()=>{
                setErrorMessage('');
                subscription.unsubscribe();
            });
        } else {
            setPasswordSetInProgress(false);
            if ((locationState === null || locationState === void 0 ? void 0 : locationState.type) === NotificationActionType.ApproveTeamMemberInvitation) {
                history.push(routes.notificationAction, locationState);
            } else {
                history.push(routes.home);
            }
        }
    });
    return /*#__PURE__*/ _jsx(UnauthorizedPageContainer, {
        children: /*#__PURE__*/ _jsxs(FormContainer, {
            onSubmit: onSubmit,
            "data-testid": "set-password-form",
            children: [
                /*#__PURE__*/ _jsx(FormTitle, {
                    children: texts.createNewPassword()
                }),
                /*#__PURE__*/ _jsx(FormFields, {
                    children: /*#__PURE__*/ _jsxs(VerticalFormFields, {
                        children: [
                            /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "password",
                                defaultValue: "",
                                rules: {
                                    required: true,
                                    validate: (value)=>validatePassword(value, [
                                            claims === null || claims === void 0 ? void 0 : claims.email,
                                            claims === null || claims === void 0 ? void 0 : claims.firstName,
                                            claims === null || claims === void 0 ? void 0 : claims.lastName
                                        ].filter(Boolean)),
                                    deps: 'confirm-password'
                                },
                                render: (param)=>{
                                    let { field: { onChange, onBlur, value }, fieldState } = param;
                                    var _fieldState_error;
                                    return /*#__PURE__*/ _jsx(PasswordField, {
                                        bi: {
                                            name: 'set_password_new'
                                        },
                                        value: value,
                                        onChange: onChange,
                                        onBlur: ()=>{
                                            logBI(10203, {
                                                isPasswordValid: !fieldState.error
                                            });
                                            onBlur();
                                        },
                                        error: !!fieldState.error,
                                        helperText: (_fieldState_error = fieldState.error) === null || _fieldState_error === void 0 ? void 0 : _fieldState_error.message,
                                        showStrengthMeter: true,
                                        strengthScore: strengthScore,
                                        label: texts.newPasswordLabel(),
                                        testId: "password-input"
                                    });
                                }
                            }),
                            /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "confirm-password",
                                defaultValue: "",
                                rules: {
                                    required: true,
                                    validate: (value)=>{
                                        const doPasswordsMatch = getValues('password') === value;
                                        return doPasswordsMatch || 'Passwords do not match';
                                    }
                                },
                                render: (param)=>{
                                    let { field: { onChange, onBlur, value }, fieldState, formState } = param;
                                    var _fieldState_error;
                                    return /*#__PURE__*/ _jsx("div", {
                                        children: /*#__PURE__*/ _jsx(PasswordField, {
                                            bi: {
                                                name: 'set_password_confirm'
                                            },
                                            value: value,
                                            onChange: onChange,
                                            onBlur: onBlur,
                                            error: !formState.isValid && !!fieldState.error,
                                            helperText: !formState.isValid && ((_fieldState_error = fieldState.error) === null || _fieldState_error === void 0 ? void 0 : _fieldState_error.message),
                                            label: texts.confirmPasswordLabel(),
                                            testId: "confirm-password-input"
                                        })
                                    });
                                }
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(SubmitButtonField, {
                    disabled: !formState.isValid,
                    testId: "set-password-button",
                    isLoading: passwordSetInProgress,
                    children: "Set Password"
                }),
                errorMessage && /*#__PURE__*/ _jsx(ErrorMessage, {
                    testId: "error-msg",
                    children: errorMessage
                })
            ]
        })
    });
}
